/*
 * Copyright (c) 2024, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useState} from 'react';
import {FormattedMessage} from 'react-intl';
import PropTypes from 'prop-types';

import LoginFields from '@salesforce/retail-react-app/app/components/forms/login-fields';
import {Button, Stack, Text, useMultiStyleConfig} from '@salesforce/retail-react-app/app/components/shared/ui';
import SocialLogin from '@salesforce/retail-react-app/app/components/social-login';
import StandardLogin from '@salesforce/retail-react-app/app/components/standard-login';
import {LOGIN_TYPES} from '@salesforce/retail-react-app/app/constants';

const PasswordlessLogin = ({
  form,
  handleForgotPasswordClick,
  handlePasswordlessLoginClick,
  isSocialEnabled = false,
  idps = [],
  setLoginType,
}) => {
  const [showPasswordView, setShowPasswordView] = useState(false);

  const handlePasswordButton = async (e) => {
    setLoginType(LOGIN_TYPES.PASSWORD);
    const isValid = await form.trigger();
    // Manually trigger the browser native form validations
    const domForm = e.target.closest('form');

    if (isValid && domForm.checkValidity()) {
      setShowPasswordView(true);
    } else {
      domForm.reportValidity();
    }
  };

  const styles = useMultiStyleConfig('FormAuth', {});

  return (
    <>
      {((!form.formState.isSubmitSuccessful && !showPasswordView) ||
        form.formState.errors.email) && (
        <Stack>
          <LoginFields
            form={form}
            handleForgotPasswordClick={handleForgotPasswordClick}
            hidePassword={true}
          />
          <Stack {...styles.buttonContainer}>
            <Stack {...styles.buttonGroup}>
              <Button
                isLoading={form.formState.isSubmitting}
                type="submit"
                onClick={() => {
                  handlePasswordlessLoginClick();
                  form.clearErrors('global');
                }}
                {...styles.buttonSubmit}
              >
                <FormattedMessage
                  defaultMessage="Continue Securely"
                  id="login_form.button.continue_securely"
                />
              </Button>
              <Button
                {...styles.buttonOutline}
                onClick={handlePasswordButton}
              >
                <FormattedMessage
                  defaultMessage="Password"
                  id="login_form.button.password"
                />
              </Button>
            </Stack>
            {isSocialEnabled && idps.length > 0 && (
              <>
                <Text {...styles.divider}>
                  <FormattedMessage
                    defaultMessage="Or continue with"
                    id="login_page.or_continue_with"
                  />
                </Text>
                <SocialLogin form={form} idps={idps} />
              </>
            )}
          </Stack>
        </Stack>
      )}
      {!form.formState.isSubmitSuccessful &&
        showPasswordView &&
        !form.formState.errors.email && (
        <StandardLogin
          form={form}
          handleForgotPasswordClick={handleForgotPasswordClick}
          hideEmail={true}
          setShowPasswordView={setShowPasswordView}
        />
      )}
    </>
  );
};

PasswordlessLogin.propTypes = {
  form: PropTypes.object,
  handleForgotPasswordClick: PropTypes.func,
  handlePasswordlessLoginClick: PropTypes.func,
  isSocialEnabled: PropTypes.bool,
  idps: PropTypes.arrayOf(PropTypes.string),
  hideEmail: PropTypes.bool,
  setLoginType: PropTypes.func,
};

export default PasswordlessLogin;
